import {
  fetchLoginConfig,
  subscribeLoginConfig,
  type LoginConfig,
} from '@/core/services/contentfulClient';
import { useEffect, useState } from 'react';

export interface LoginConfigState {
  /** `true` while the Contentful request is in-flight. */
  isLoading: boolean;
  /**
   * Resolved config values. `null` while loading or when Contentful is
   * unavailable. When `null`, callers should default to showing all buttons.
   */
  config: LoginConfig | null;
}

/**
 * Fetches the login feature-flag config from Contentful (`config` content
 * type + `config` tag) once on mount.
 *
 * - While loading → `config` is `null`; show all login options optimistically.
 * - On success → apply `isUAEpassLogin` / `isEmiratesIdLogin` flags.
 * - On failure → `config` stays `null`; fall back to showing all options.
 */
export function useLoginConfig(): LoginConfigState {
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState<LoginConfig | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadLoginConfig = async () => {
      try {
        const result = await fetchLoginConfig();
        if (!cancelled) {
          setConfig(result);
        }
      } catch {
        // Contentful unavailable: keep config null so all login options stay visible.
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadLoginConfig();

    // A later Contentful refresh (app back in foreground) updates the buttons.
    const unsubscribe = subscribeLoginConfig((next) => {
      if (!cancelled) {
        setConfig(next);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return { isLoading, config };
}
